// OIDC client wrapper around @azure/msal-browser. Entra External ID is
// the production IdP; the same MSAL config works against a workforce
// Entra ID tenant too. Mode toggle via VITE_AUTH_MODE:
//
//   permissive  → no OIDC, the auth store's tenantId input drives the
//                 x-lymon-tenant-id header. Default for dev.
//   oidc        → OIDC + Bearer tokens. Required for staging / prod.
//
// MSAL is loaded lazily so the permissive build doesn't pull msal-browser
// into the gzipped bundle. The OIDC path adds ~30 kB minified.

import type {
  AccountInfo,
  AuthenticationResult,
  PublicClientApplication,
} from '@azure/msal-browser';

export interface OidcConfig {
  authority: string;       // e.g. 'https://lybits.ciamlogin.com/<TENANT_ID>/v2.0'
  clientId: string;        // SPA app registration GUID
  apiScope: string;        // e.g. 'api://lymon-gateway/access_as_user'
  redirectUri: string;     // must be registered in the App registration
}

let msalInstance: PublicClientApplication | null = null;
let cfg: OidcConfig | null = null;

export function isOidcEnabled(): boolean {
  return import.meta.env.VITE_AUTH_MODE === 'oidc';
}

function loadConfig(): OidcConfig {
  if (cfg) return cfg;
  const authority = import.meta.env.VITE_OIDC_AUTHORITY as string | undefined;
  const clientId = import.meta.env.VITE_OIDC_CLIENT_ID as string | undefined;
  const apiScope = import.meta.env.VITE_OIDC_API_SCOPE as string | undefined;
  const redirectUri = import.meta.env.VITE_OIDC_REDIRECT_URI as string | undefined;
  if (!authority || !clientId || !apiScope) {
    throw new Error(
      "VITE_AUTH_MODE=oidc requires VITE_OIDC_AUTHORITY + VITE_OIDC_CLIENT_ID + VITE_OIDC_API_SCOPE. " +
        "See docs/internal/21-entra-setup.md for the values to put in your .env.local.",
    );
  }
  cfg = {
    authority,
    clientId,
    apiScope,
    redirectUri: redirectUri ?? window.location.origin,
  };
  return cfg;
}

async function getInstance(): Promise<PublicClientApplication> {
  if (msalInstance) return msalInstance;
  const c = loadConfig();
  const { PublicClientApplication: MsalApp, LogLevel } = await import('@azure/msal-browser');
  msalInstance = new MsalApp({
    auth: {
      clientId: c.clientId,
      authority: c.authority,
      redirectUri: c.redirectUri,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      // localStorage so a refresh keeps the session. The token itself is
      // short-lived (1h); the refresh token cookie is what bridges page
      // reloads. sessionStorage would log the user out on a tab close —
      // unfriendly for a SPA they'll likely keep open all day.
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        logLevel: import.meta.env.DEV ? LogLevel.Info : LogLevel.Warning,
        piiLoggingEnabled: false,
        loggerCallback: (level, message) => {
          // eslint-disable-next-line no-console
          console.log(`[msal:${level}]`, message);
        },
      },
    },
  });
  await msalInstance.initialize();
  return msalInstance;
}

export interface UserSession {
  /** The Entra account record — has username (email), name, tenant id, etc. */
  account: AccountInfo;
  /** Bearer access token for the lymon-gateway API. */
  accessToken: string;
  /** The tenant_id claim — populated by Entra from the user flow. */
  tenantId: string;
  /** Unix epoch ms when accessToken expires. */
  expiresAtMs: number;
}

/**
 * Bootstrap call — invoke on app start. Handles the redirect callback
 * if msal stored a pending request, otherwise tries silent SSO.
 * Returns the session if one is available, null if the user needs to
 * call signIn().
 */
export async function bootstrap(): Promise<UserSession | null> {
  const msal = await getInstance();
  // Pick up any pending redirect (when we land at /?code=… from Entra).
  await msal.handleRedirectPromise();

  const accounts = msal.getAllAccounts();
  if (accounts.length === 0) return null;
  msal.setActiveAccount(accounts[0]!);
  return acquireToken();
}

export async function signIn(): Promise<void> {
  const msal = await getInstance();
  const c = loadConfig();
  // loginRedirect navigates away — control returns via bootstrap()
  // after Entra bounces back to redirectUri.
  await msal.loginRedirect({
    scopes: ['openid', 'profile', 'email', c.apiScope],
  });
}

export async function signOut(): Promise<void> {
  const msal = await getInstance();
  await msal.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
}

async function acquireToken(): Promise<UserSession | null> {
  const msal = await getInstance();
  const c = loadConfig();
  const account = msal.getActiveAccount();
  if (!account) return null;

  let result: AuthenticationResult;
  try {
    result = await msal.acquireTokenSilent({
      scopes: [c.apiScope],
      account,
    });
  } catch (err) {
    // Silent acquisition can fail when refresh tokens expire (typically
    // ≥ 90 days). Fall back to interactive — user sees the Entra page
    // briefly then is bounced back authenticated.
    // eslint-disable-next-line no-console
    console.warn('[oidc] silent token failed; redirecting', err);
    await msal.acquireTokenRedirect({ scopes: [c.apiScope], account });
    return null; // navigation in-flight
  }

  // Extract tenant_id from the id token claims. Entra External ID
  // adopts the extensionAttribute_tenantId pattern; workforce Entra
  // uses 'tid'. We try a few common names so the same code works
  // against either.
  const claims = result.idTokenClaims as Record<string, unknown> | undefined;
  const tenantId =
    (claims?.extension_lymon_tenant_id as string | undefined) ??
    (claims?.lymon_tenant_id as string | undefined) ??
    (claims?.tid as string | undefined) ??
    'default';

  return {
    account,
    accessToken: result.accessToken,
    tenantId,
    expiresAtMs: result.expiresOn?.getTime() ?? Date.now() + 60 * 60 * 1000,
  };
}

/**
 * Forces a fresh token. Use when the API returns 401 (token may be on
 * the boundary of expiry).
 */
export async function refreshToken(): Promise<UserSession | null> {
  return acquireToken();
}
